import { useState, useEffect } from "react";

// Crash-proof hash router hooks for wouter, using a SHARED module-level store.
//
// Why: some browser extensions monkey-patch window.addEventListener (only the
// global one — React still mounts because it binds listeners on DOM elements).
// wouter's built-in hooks subscribe via window.addEventListener inside
// useSyncExternalStore, so that break throws "n is not a function" during render
// and white-screens the app.
//
// These hooks: (1) guard every browser API in try/catch, (2) share one store so
// all components re-render together, and (3) notify subscribers manually on
// navigate — so routing works even when addEventListener/pushState are broken.

const readHash = () => "/" + (window.location.hash || "").replace(/^#?\/?/, "");
const readSearch = () => (window.location.search || "").replace(/^\?/, "");

const subscribers = new Set<() => void>();
let globalAttached = false;

function emit() {
  subscribers.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore a bad subscriber */
    }
  });
}

function ensureGlobalListener() {
  if (globalAttached) return;
  globalAttached = true;
  try {
    window.addEventListener("hashchange", emit);
    window.addEventListener("popstate", emit);
  } catch {
    /* addEventListener broken by an extension — navigation still works via emit() */
  }
}

function useStoreValue(read: () => string): string {
  const [val, setVal] = useState(read);
  useEffect(() => {
    const update = () => setVal(read());
    subscribers.add(update);
    ensureGlobalListener();
    update(); // reconcile in case it changed between render and effect
    return () => {
      subscribers.delete(update);
    };
  }, []);
  return val;
}

export function useSafeHashLocation(): [string, (to: string, opts?: { replace?: boolean }) => void] {
  const loc = useStoreValue(readHash);

  const navigate = (to: string, opts?: { replace?: boolean }) => {
    const [path, search] = String(to).replace(/^#?\/?/, "").split("?");
    const target = "/" + path;
    try {
      const url = new URL(window.location.href);
      url.hash = target;
      url.search = search ? "?" + search : "";
      if (opts?.replace) window.history.replaceState(null, "", url.href);
      else window.history.pushState(null, "", url.href);
    } catch {
      try {
        window.location.hash = target;
      } catch {
        /* ignore */
      }
    }
    // Notify ALL components immediately, regardless of whether events fired.
    emit();
  };

  return [loc, navigate];
}

export function useSafeSearch(): string {
  return useStoreValue(readSearch);
}

// wouter integration hooks (hash variant — kept for reference/back-compat).
(useSafeHashLocation as any).hrefs = (href: string) => "#" + href;
(useSafeHashLocation as any).searchHook = useSafeSearch;

/* ---------------------------------------------------------------------------
   Clean-URL (path-based) variant — same crash-proof store, no leading '#'.
   Deep links like /project/:id resolve directly (nginx SPA fallback serves
   index.html for unknown paths).
--------------------------------------------------------------------------- */

const readPath = () => window.location.pathname || "/";

export function useSafeBrowserLocation(): [string, (to: string, opts?: { replace?: boolean }) => void] {
  const loc = useStoreValue(readPath);

  const navigate = (to: string, opts?: { replace?: boolean }) => {
    try {
      if (opts?.replace) window.history.replaceState(null, "", to);
      else window.history.pushState(null, "", to);
    } catch {
      try { window.location.assign(to); } catch { /* ignore */ }
    }
    // Notify ALL components immediately, regardless of whether popstate fired.
    emit();
  };

  return [loc, navigate];
}
(useSafeBrowserLocation as any).hrefs = (href: string) => href;
(useSafeBrowserLocation as any).searchHook = useSafeSearch;

/** One-time migration: turn a legacy hash route (#/project/x?y) into a clean URL. */
export function migrateLegacyHashUrl(): void {
  try {
    const h = window.location.hash || "";
    if (h.startsWith("#/")) {
      const target = h.slice(1); // "/project/x" or "/marketplace?tab=People"
      const [path, q] = target.split("?");
      const search = q ? "?" + q : window.location.search;
      window.history.replaceState(null, "", path + search);
    }
  } catch { /* ignore */ }
}
